import React from "react";
import FormLabel from "./FormLabel";
import FormFieldError, { fieldClass } from "./FormFieldError";
import SelfPaymentDisplay from "./SelfPaymentDisplay";
import PaymentQrCode from "./PaymentQrCode";
import {
  combinedPaymentValue,
  DEFAULT_PAYMENT_INSTRUCTIONS,
  resolveFormSelfPayment,
  splitPaymentValue,
} from "../lib/payment";

/**
 * Shared fields for host default payment settings and per-event overrides.
 * `values` shape matches account.default_payment + use_profile_defaults when used on create event.
 */
export default function SelfPaymentForm({
  values,
  onChange,
  account,
  organizer,
  pricing,
  payeeName,
  showProfileToggle = false,
  showQrPreview = true,
  idPrefix = "payment",
  error,
  fieldErrors = {},
  onHostProfileClick,
}) {
  account = account ?? organizer;
  function update(k, v) {
    onChange({ ...values, [k]: v });
  }

  function updatePayment(raw) {
    onChange({ ...values, ...splitPaymentValue(raw) });
  }

  const useProfileDefaults = showProfileToggle && values.use_profile_defaults !== false;
  const profilePayment = useProfileDefaults ? resolveFormSelfPayment(values, account) : values;

  return (
    <div
      id="event-payment-form"
      tabIndex={error ? -1 : undefined}
      aria-invalid={error ? true : undefined}
      className="space-y-4 outline-none"
      data-testid={`${idPrefix}-form`}
    >
      {error ? <FormFieldError id="event-payment-form-error">{error}</FormFieldError> : null}
      {showProfileToggle && (
        <label className="flex items-start gap-3 cursor-pointer border-t border-black/[0.08] pt-3">
          <input
            type="checkbox"
            checked={values.use_profile_defaults !== false}
            onChange={(e) => update("use_profile_defaults", e.target.checked)}
            className="w-5 h-5 accent-plum mt-0.5 shrink-0"
            data-testid={`${idPrefix}-use-profile-checkbox`}
          />
          <div className="min-w-0">
            <p className="font-ui font-medium text-[15px]">Use my profile payment settings</p>
            <p className="font-ui text-[13px] font-medium text-ink/80 leading-relaxed mt-0.5">
              Same link, instructions, and contact info across all your events. Uncheck to customise for this event only.
            </p>
          </div>
        </label>
      )}

      {useProfileDefaults ? (
        <SelfPaymentDisplay
          selfPayment={profilePayment}
          pricing={pricing}
          payeeName={payeeName}
          variant="profile"
          idPrefix={idPrefix}
          onHostProfileClick={onHostProfileClick}
        />
      ) : (
        <div className="space-y-4">
          <label className="block">
            <FormLabel className="block mb-2">Payment link or ID</FormLabel>
            <input
              className="field"
              value={combinedPaymentValue(values)}
              onChange={(e) => updatePayment(e.target.value)}
              placeholder="https://paypal.me/you, Revolut link, name@upi, @handle…"
              data-testid={`${idPrefix}-link-input`}
            />
          </label>
          <p className="font-ui text-[13px] font-medium text-ink/80 leading-relaxed -mt-2">
            PayPal, Revolut, UPI ID, bank link, or other handle. Guests scan the QR to pay.
          </p>

          {showQrPreview && combinedPaymentValue(values) && (
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <PaymentQrCode
                selfPayment={values}
                pricing={pricing}
                payeeName={payeeName}
              />
              <p className="font-ui text-[13px] font-medium text-ink/80 leading-relaxed max-w-xs">
                Preview of the QR guests will see on your event page.
              </p>
            </div>
          )}

          <label className="block">
            <FormLabel className="block mb-2">Payment instructions</FormLabel>
            <textarea
              className="field min-h-[100px]"
              value={values.instructions || ""}
              onChange={(e) => update("instructions", e.target.value)}
              placeholder={DEFAULT_PAYMENT_INSTRUCTIONS}
              data-testid={`${idPrefix}-instructions-input`}
            />
          </label>

          <div>
            <FormLabel className="block mb-2">Get in touch</FormLabel>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="block">
                <FormLabel className="block mb-2">WhatsApp no</FormLabel>
                <input
                  type="tel"
                  className={fieldClass(fieldErrors.contact_phone)}
                  value={values.contact_phone || ""}
                  onChange={(e) => update("contact_phone", e.target.value)}
                  placeholder="+49 170 … or 170 …"
                  autoComplete="tel"
                  aria-invalid={Boolean(fieldErrors.contact_phone)}
                  aria-describedby={
                    fieldErrors.contact_phone ? `${idPrefix}-phone-input-error` : undefined
                  }
                  data-testid={`${idPrefix}-phone-input`}
                />
                {fieldErrors.contact_phone ? (
                  <FormFieldError id={`${idPrefix}-phone-input-error`}>
                    {fieldErrors.contact_phone}
                  </FormFieldError>
                ) : null}
              </label>
              <label className="block">
                <FormLabel className="block mb-2">Email</FormLabel>
                <input
                  type="email"
                  className={fieldClass(fieldErrors.contact_email)}
                  value={values.contact_email || ""}
                  onChange={(e) => update("contact_email", e.target.value)}
                  placeholder="host@example.com"
                  autoComplete="email"
                  aria-invalid={Boolean(fieldErrors.contact_email)}
                  aria-describedby={
                    fieldErrors.contact_email ? `${idPrefix}-email-input-error` : undefined
                  }
                  data-testid={`${idPrefix}-email-input`}
                />
                {fieldErrors.contact_email ? (
                  <FormFieldError id={`${idPrefix}-email-input-error`}>
                    {fieldErrors.contact_email}
                  </FormFieldError>
                ) : null}
              </label>
              <label className="block">
                <FormLabel className="block mb-2">Telegram</FormLabel>
                <input
                  className="field"
                  value={values.contact_telegram || ""}
                  onChange={(e) => update("contact_telegram", e.target.value)}
                  placeholder="@username"
                  data-testid={`${idPrefix}-telegram-input`}
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
